import React, { useState, useEffect } from 'react';
import AuthService from '../services/AuthService';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '../components/Loader';
import logo from '../assets/images/logo.png';
import './PartnerLogin.css';

const slides = [
  {
    icon: 'bi-graph-up-arrow',
    title: 'Grow Your Business',
    description: 'Manage bookings, staff, and services from one powerful partner dashboard tailored for your partner.',
  },
  {
    icon: 'bi-calendar2-check',
    title: 'Real-Time Bookings',
    description: 'Stay on top of every appointment with live notifications and a clear calendar overview.',
  },
  {
    icon: 'bi-bar-chart-line',
    title: 'Insightful Reports',
    description: 'Track revenue, customer retention, and team performance with easy-to-read analytics.',
  },
];

const PartnerLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const [isLoader, setIsLoader] = useState(false);
  const [requiresOTP, setRequiresOTP] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (AuthService.getCurrentpartner()) navigate('/partner/dashboard');
  }, [navigate]);

  useEffect(() => {
    const interval = setInterval(() => setCurrentSlide((p) => (p + 1) % slides.length), 5000);
    return () => clearInterval(interval);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoader(true);
    try {
      const data = await AuthService.partnerLogin(email, password, otp);
      if (data.status) {
        toast.success(data.message);
        const partner = { ...data.data.partner, auth_token: data.data.auth_token };
        localStorage.setItem('partner', JSON.stringify(partner));
        window.location.href = process.env.REACT_APP_BASE_PATH + '/partner/dashboard';
      } else {
        if (data.requires_otp) {
          setRequiresOTP(true);
          toast.info(data.message || 'OTP sent to your email');
        } else {
          toast.error(data.message);
        }
      }
    } catch {
      toast.error('Login failed. Please try again.');
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <div className="cp-container">

      {/* ── Left panel ── */}
      <div className="cp-left">
        <div className="cp-left-inner">
      

          {/* Slide */}
          <div className="cp-slide-area">
            <div className="cp-slide-icon">
              {/* Logo */}
              <div className="cp-logo">
                <img src={logo} alt="LMTKOM" />
              </div>

            </div>
            <h2 className="cp-slide-title">{slides[currentSlide].title}</h2>
            <p className="cp-slide-desc">{slides[currentSlide].description}</p>
          </div>

          {/* Dots */}
          <div className="cp-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`cp-dot ${i === currentSlide ? 'active' : ''}`}
                onClick={() => setCurrentSlide(i)}
              />
            ))}
          </div>

          {/* Feature chips */}
          <div className="cp-chips">
            {['Booking Management', 'Services Management', 'Revenue Insights'].map((label) => (
              <span key={label} className="cp-chip">
                <i className="bi bi-check-circle-fill me-1 text-white"></i>{label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="cp-right">
        <div className="cp-form-wrap">

          <div className="cp-form-header">
            <img src={logo} alt="LMT" className="cp-form-logo" />
            <h2 className="cp-form-title">Partner Portal</h2>
            <p className="cp-form-subtitle">Sign in to manage your business</p>
          </div>

          <form onSubmit={handleLogin} className="cp-form" noValidate>

            {/* Email */}
            <div className="cp-field">
              <label className="cp-label">Email Address</label>
              <div className="cp-input-wrap">
                <i className="bi bi-envelope cp-input-icon"></i>
                <input
                  type="email"
                  required
                  autoComplete="off"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="cp-input"
                  placeholder="partner@partner.com"
                />
              </div>
            </div>

            {/* Password */}
            <div className="cp-field">
              <label className="cp-label">Password</label>
              <div className="cp-input-wrap">
                <i className="bi bi-lock cp-input-icon"></i>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="off"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="cp-input cp-input-pw"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="cp-pw-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                >
                  <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                </button>
              </div>
            </div>

            {/* OTP */}
            {requiresOTP && (
              <div className="cp-field">
                <label className="cp-label">Verification Code</label>
                <div className="cp-input-wrap">
                  <i className="bi bi-shield-lock cp-input-icon"></i>
                  <input
                    type="text"
                    required
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="cp-input"
                    placeholder="6-digit OTP"
                    maxLength="6"
                    autoComplete="off"
                  />
                </div>
                <p className="cp-otp-hint">A verification code has been sent to your email.</p>
              </div>
            )}

            <button type="submit" disabled={isLoader} className="cp-submit">
              {isLoader
                ? <Loader size="20" text="Signing in" color="white" />
                : <><i className="bi bi-box-arrow-in-right me-2 text-white"></i>Sign In</>
              }
            </button>

          </form>

          <p className="cp-terms">
            By signing in you agree to our{' '}
            <a href="#" className="cp-terms-link">Terms of Service</a> and{' '}
            <a href="#" className="cp-terms-link">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PartnerLogin;
